// Shared orientation for WonderLabs' nine phases and a phase's activities.
// It occupies one short line; the detailed overview floats over, never pushes
// the learning workspace down. Existing progression state remains authoritative.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Platform, ScrollView, Text, TouchableOpacity, View } from "react-native";
import { createPortal } from "react-dom";

const DONE = "#258C75";
const POPOVER_MAX_WIDTH = 930;
const POPOVER_HEIGHT = 260;

export default function WonderLabsProgressNavigator({
  label,
  currentIndex = 0,
  items = [],
  onSelect,
  expanded = false,
  onExpandedChange,
  accent = "#076CA8",
  border = "#CFD9E5",
  text = "#13263D",
  muted = "#516579",
  surface = "#FFFFFF",
  background = "#F6FAFD",
  compact = false,
}) {
  const triggerRef = useRef(null);
  const hideTimer = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(expanded || hovered);
  const count = items.length;
  const current = items[currentIndex];
  const isWeb = Platform.OS === "web" && typeof window !== "undefined" && typeof document !== "undefined";

  const measurePopover = useCallback(() => {
    if (!isWeb) return;
    const node = triggerRef.current;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(POPOVER_MAX_WIDTH, Math.max(280, viewportWidth - 24));
    const update = (x, y, w, h) => {
      const left = Math.max(12, Math.min(x, viewportWidth - width - 12));
      const roomBelow = viewportHeight - (y + h);
      const top = roomBelow < POPOVER_HEIGHT + 16 && y > POPOVER_HEIGHT + 16
        ? Math.max(12, y - POPOVER_HEIGHT - 4) : y + h + 2;
      setAnchor({ left, top, width });
    };
    if (node?.getBoundingClientRect) {
      const r = node.getBoundingClientRect();
      update(r.left, r.top, r.width, r.height);
    } else if (node?.measureInWindow) {
      node.measureInWindow(update);
    } else {
      update(12, 40, viewportWidth - 24, 36);
    }
  }, [isWeb]);

  useEffect(() => {
    if (!open || !isWeb) return undefined;
    measurePopover();
    const dismiss = (event) => { if (event.key === "Escape") { setHovered(false); onExpandedChange?.(false); } };
    window.addEventListener("keydown", dismiss);
    window.addEventListener("resize", measurePopover);
    document.addEventListener("scroll", measurePopover, true);
    return () => {
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("resize", measurePopover);
      document.removeEventListener("scroll", measurePopover, true);
    };
  }, [open, isWeb, measurePopover, onExpandedChange]);
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = null;
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setHovered(false), 180);
  };
  const choose = (idx) => {
    if (!items[idx] || items[idx].locked) return;
    if (idx !== currentIndex) onSelect?.(idx);
    setHovered(false);
    onExpandedChange?.(false);
  };

  const detail = (
    <View style={{ padding: 10, borderRadius: 12, backgroundColor: surface,
      borderColor: border, borderWidth: 1, maxHeight: POPOVER_HEIGHT }}>
      <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <Text style={{ fontWeight: "800", color: text, fontSize: 13 }}>{label} · {currentIndex + 1} of {count}</Text>
        <TouchableOpacity accessibilityRole="button" accessibilityLabel="Close progress details"
          onPress={() => { setHovered(false); onExpandedChange?.(false); }} style={{ paddingHorizontal: 10, paddingVertical: 4 }}>
          <Text style={{ color: accent, fontWeight: "800" }}>Close ×</Text>
        </TouchableOpacity>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator
        contentContainerStyle={{ gap: 8, paddingBottom: 5, alignItems: "stretch" }}>
        {items.map((item, idx) => {
          const selected = idx === currentIndex;
          const locked = Boolean(item.locked);
          return (
            <TouchableOpacity key={item.id || idx} accessibilityRole="button"
              accessibilityState={{ selected, disabled: locked }} disabled={locked}
              accessibilityLabel={`${idx + 1}. ${item.title}. ${item.complete ? "Completed" : selected ? "Current" : locked ? "Upcoming, locked" : "Available"}`}
              onPress={() => choose(idx)}
              style={{ width: 165, minHeight: 94, paddingHorizontal: 10,
                paddingVertical: 9, borderRadius: 10, borderWidth: 1,
                borderColor: selected ? accent : border,
                backgroundColor: selected ? background : surface }}>
              <Text style={{ color: item.complete ? DONE : selected ? accent : muted,
                fontWeight: "800", fontSize: 12 }}>
                {item.complete ? "✓" : locked ? "🔒" : selected ? "●" : "○"} · {item.shortLabel || `Step ${idx + 1}`}
              </Text>
              <Text style={{ marginTop: 5, color: locked ? muted : text,
                fontWeight: selected ? "800" : "600", fontSize: 12, lineHeight: 17 }}>
                {item.title}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  return (
    <View ref={triggerRef} collapsable={false}
      onMouseEnter={isWeb ? () => { cancelHide(); setHovered(true); } : undefined}
      onMouseLeave={isWeb ? scheduleHide : undefined}
      style={{ position: "relative", width: "100%" }}>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={`${label}: ${currentIndex + 1} of ${count}. ${current?.title || ""}. Show overview`}
        onPress={() => {
          cancelHide();
          if (open) { setHovered(false); onExpandedChange?.(false); }
          else onExpandedChange?.(true);
        }}
        style={{ minHeight: 38, flexDirection: "row", alignItems: "center", gap: 10,
          paddingHorizontal: compact ? 9 : 12, paddingVertical: 7,
          backgroundColor: surface, borderBottomWidth: 1, borderColor: border }}>
        <Text style={{ color: muted, fontWeight: "800", fontSize: 11 }} numberOfLines={1}>
          {label.toUpperCase()} · {currentIndex + 1}/{count}
        </Text>
        <View style={{ flex: 1, minWidth: 0, flexDirection: "row", gap: 4 }}>
          {items.map((item, idx) => (
            <View key={item.id || idx} style={{ flex: 1, minWidth: 3, height: 6, borderRadius: 6,
              backgroundColor: item.complete ? DONE : idx === currentIndex ? accent : border }} />
          ))}
        </View>
        <Text style={{ color: accent, fontWeight: "900", fontSize: 13 }}>{open ? "⌃" : "⌄"}</Text>
      </TouchableOpacity>
      {open && count > 0 && isWeb && anchor ? createPortal(
        <div onMouseEnter={cancelHide} onMouseLeave={scheduleHide}
          style={{ position: "fixed", zIndex: 99999, left: anchor.left, top: anchor.top,
            width: anchor.width, boxShadow: "0 12px 32px rgba(13,38,63,0.20)", borderRadius: 12 }}>
          {detail}
        </div>, document.body) : null}
      {open && count > 0 && !isWeb ? (
        <View style={{ position: "absolute", top: 38, left: 0, right: 0, zIndex: 999, elevation: 10 }}>{detail}</View>
      ) : null}
    </View>
  );
}
